import PropTypes from 'prop-types';
import React from 'react';

import { Header, SideLine, Footer, Background } from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { PersonItem } from '$partials/listings';
import { DetailDialogProvider } from '$pages/detail/detail-page-provider';
import { getBackgroundColours, getSiteContext } from '$utilities/site-context';
import { formatUrlWithActiveFund } from '$utilities/format';
import { useMultiSelectFilter } from '$hooks/filters';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const PeopleListingPage = (props) => {
    const page = props.data.page;
    const listingNavigation = getSiteContext()?.peopleListingNavigation;

    const fundsFilter = useMultiSelectFilter(page.items?.filters?.funds);

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        page: page?.items?.pageInfo?.page,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'funds': fundsFilter.value?.length > 0 ? fundsFilter.value : null,
        }
    });

    return (
        <>
            <Background {...getBackgroundColours()} />

            {/* Navigation */}
            <Header navigation={props.data.navigation}/>
            {/* Spacer */}
            <div className='pt-40 sm:pt-50 md:pt-80'></div>

            <main
                className='min-h-screen relative'
                id='main-content'>
                <SideLine/>

                <div className='layout-container'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Heading */}
                        <div className='sm:w-9/12 layout-item-padding'>
                            <h2 className='type-style-2'>
                                {props.data?.peopleListingNavigation?.heading}
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Spacer */}
                <div className='mt-60'/>

                {/* Links */}
                {listingNavigation?.length > 0 &&
                    <div className='layout-container'>
                        <div className='overflow-x-scroll -mr-20 no-scrollbar'>
                            <div className='border-b border-b-black/10 w-max mx-12'>
                                <div className='flex pb-15 -mx-12'>
                                    {listingNavigation?.filter(Boolean)?.map(({page, label}, i, {length}) => {
                                        if (!page) { return null; }
                                        const url = formatUrlWithActiveFund(page.url);
                                        return (
                                            <a
                                                className={`type-style-8 flex-shrink-0 px-12 py-10 mx-5 rounded-full hover:bg-white transition-colors duration-200 ease ${i + 1 === length ? 'mr-0' : ''} ${ url.includes(props.data.uri) ? 'bg-white' : ''}`}
                                                href={url}
                                                key={i}>
                                                {label ? label : page.title}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                }

                <div className='layout-container-padding-right flex flex-wrap max-w-container mt-40 sm:pb-70 md:pb-100 rounded-md'>
                    {/* Left Sidebar */}
                    <div className='w-sidebar'>
                        <div className='flex flex-col items-center'>
                            <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl'>
                                {page?.title}
                            </h2>
                        </div>
                    </div>

                    {/* Listing */}
                    <DetailDialogProvider>
                        <AjaxListingContext.Provider value={listing}>
                            <AjaxListingContext.Consumer>
                                {(listingState) => (
                                    <>
                                        <section
                                            aria-busy={listingState.loading}
                                            aria-label='People'
                                            className='flex-1 flex flex-wrap self-start'
                                            role='feed'>
                                            {listingState?.items?.length > 0 ? (
                                                <>
                                                    <ul className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 layout-gap gap-y-30 sm:gap-y-25 md:gap-y-30'>
                                                        {listingState.items.map(({node: item}) => {
                                                            return (
                                                                <li
                                                                    key={item.id}>
                                                                    <PersonItem
                                                                        itemsAreClickable={page?.itemsAreClickable}
                                                                        openToModal={page?.openItemsInModal}
                                                                        {...item}/>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>

                                                    {/* Load more button */}
                                                    <InfinitePagination className='mt-20'>
                                                        {'Loading...'}
                                                    </InfinitePagination>
                                                </>
                                            ): (() => {
                                                /* No results message */
                                                return (
                                                    <>
                                                        <p>
                                                            {'No results were found'}
                                                        </p>
                                                    </>
                                                );
                                            })()}
                                        </section>
                                    </>
                                )}
                            </AjaxListingContext.Consumer>
                        </AjaxListingContext.Provider>
                    </DetailDialogProvider>
                </div>
            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer}/>
            )}
        </>
    );
};


/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            title: PropTypes.string.isRequired,
            openItemsInModal: PropTypes.bool,
            itemsAreClickable: PropTypes.bool,
            url: PropTypes.string,
            items: PropTypes.object,
        }),
        uri: PropTypes.string,
        navigation: PropTypes.object,
        footer: PropTypes.object,
        peopleListingNavigation: PropTypes.shape({
            heading: PropTypes.string,
            navigation: PropTypes.arrayOf(PropTypes.shape({
                page: PropTypes.shape({
                    title: PropTypes.string,
                    url: PropTypes.string,
                }),

            }),),
        }),
    }),
    domain: PropTypes.string,
};

PeopleListingPage.propTypes = propTypes;

export default PeopleListingPage;
